import { useRef, useState } from "react";
import BiodataPreview from "./BiodataPreview";
import html2canvas from "html2canvas";
import TemplateSelector from "./TemplateSelector";
import jsPDF from "jspdf";

export default function BiodataContainer({
  sections,
  template,
  userImage,
  onTemplateChange,
}) {
  const previewRef = useRef();

  const [imgLoaded, setImgLoaded] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewImages, setPreviewImages] = useState([]); // ✅ store preview images

  // ================= COMMON FUNCTION (REUSE) =================
  const generateCanvasPages = async () => {
    if (!previewRef.current) return [];

    const pages = previewRef.current.querySelectorAll(".bd-page");

    window.scrollTo(0, 0);

    let images = [];

    for (let i = 0; i < pages.length; i++) {
      const originalTransform = pages[i].style.transform;
      pages[i].style.transform = "none";

      const canvas = await html2canvas(pages[i], {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        scrollY: -window.scrollY,
        windowWidth: 1200,
        width: pages[i].scrollWidth,
        height: pages[i].scrollHeight,
      });

      pages[i].style.transform = originalTransform;

      if (canvas.width === 0 || canvas.height === 0) {
        alert("Canvas empty!");
        return [];
      }

      images.push(canvas.toDataURL("image/png"));
    }

    return images;
  };

  // ================= PDF DOWNLOAD =================
  const downloadPDF = async () => {
    if (!imgLoaded) {
      alert("Please wait until the template image is fully loaded");
      return;
    }

    const images = await generateCanvasPages();
    if (!images.length) return;

    const pdf = new jsPDF("p", "pt", "a4");

    images.forEach((img, i) => {
      const imgProps = pdf.getImageProperties(img);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

      if (i > 0) pdf.addPage();
      pdf.addImage(img, "PNG", 0, 0, pdfWidth, pdfHeight);
    });

    pdf.save("biodata.pdf");
  };

  // ================= IMAGE DOWNLOAD =================
  const downloadImage = async () => {
    if (!imgLoaded) {
      alert("Please wait until the template image is fully loaded");
      return;
    }

    const images = await generateCanvasPages();
    if (!images.length) return;

    images.forEach((img, i) => {
      const link = document.createElement("a");
      link.href = img;
      link.download = `wd_biodata${i + 1}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };

  // ================= PREVIEW GENERATE =================
  const generatePreview = async () => {
    if (!imgLoaded) {
      alert("Please wait until the template image is fully loaded");
      return;
    }

    const images = await generateCanvasPages();
    if (!images.length) return;

    setPreviewImages(images);
    setShowPreview(true);
  };

  return (
    <div className="preview-outer">
      {/* ================= MAIN PREVIEW (HIDDEN SOURCE) ================= */}
      <div className="preview-scale shadow-sm">
        <div ref={previewRef} className="maincontainer">
          <BiodataPreview
            sections={sections}
            template={template}
            userImage={userImage}
            onImgLoad={() => setImgLoaded(true)}
          />
        </div>
      </div>

      {/* ================= BUTTONS ================= */}
      <div className="bg-white bd_download_sect p-3 mt-3 mb-4 rounded-lg text-center shadow-sm d-flex justify-content-between gap-2 ms-auto biodata_action_control">
        <button
          className="btn btn-md btn-outline-primary"
          onClick={() => setShowModal(true)}
        >
          Change Template
        </button>

        <button
            className="btn btn-md btn-dark biodatak_preview_btn"
            onClick={generatePreview}
          >
            Biodata Preview
          </button>

        <div className="bd_download_b">
          <button className="btn btn-md btn-primary mx-2" onClick={downloadImage}>
            Download Image
          </button>

          <button className="btn btn-md btn-primary" onClick={downloadPDF}>
            Download PDF
          </button>

          
          
        </div>
      </div>

      {/* ================= TRUST SECTION ================= */}
      <div className="trust-card shadow-sm">
        <div>🛡️ <strong>No data is stored on server</strong></div>
        <div>⚡ <strong>Biodata is generated instantly</strong></div>
        <div>🔒 <strong>100% private & secure</strong></div>
      </div>

      {/* ================= TEMPLATE MODAL ================= */}
      {showModal && (
        <>
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Select Biodata Template</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowModal(false)}
                  >
                    <span>&times;</span>
                  </button>
                </div>

                <div className="modal-body">
                  <TemplateSelector
                    selected={template}
                    onSelect={(tpl) => {
                      onTemplateChange(tpl);
                      setShowModal(false);
                      setImgLoaded(false);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show"></div>
        </>
      )}

      {/* ================= PDF PREVIEW MODAL ================= */}
      {showPreview && (
        <>
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content">

                <div className="modal-header">
                  <h5 className="modal-title">PDF Preview</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowPreview(false)} >
                    <span>&times;</span>
                  </button>
                </div>

                <div
                  className="modal-body text-center"
                  style={{ background: "#eaeaea" }}  >
                  {previewImages.map((img, index) => (
                    <div key={index} style={{ marginBottom: "20px" }}>
                      <img
                        src={img}
                        alt={`page-${index}`}
                        style={{
                          width: "100%",
                          maxWidth: "800px",
                          background: "#fff",
                          boxShadow: "0 0 10px rgba(0,0,0,0.2)",
                        }}  />
                      <div style={{ marginTop: "5px", fontWeight: "bold" }}>
                        Page {index + 1}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
}